import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import { Link, useSearchParams } from "react-router-dom";
import { FETCH_SHOPIFY_ORDERS } from "./graphql";
import OrderLineItemsProductView from "./OrderLineItemsProductView";

const PAGE_SIZE = 30;
const ORDER_STATUS_FILTERS = {
  any: "status:any",
  open: "status:open",
  closed: "status:closed",
  cancelled: "status:cancelled",
};

function parseResponse(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeOrdersPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { orders: [], pageInfo: {}, endCursor: null };
  }

  const ordersConnection = payload?.data?.orders || payload?.orders || {};
  const edges = Array.isArray(ordersConnection?.edges)
    ? ordersConnection.edges
    : [];
  const orders = edges.map((edge) => edge?.node).filter(Boolean);
  const pageInfo = ordersConnection?.pageInfo || {};
  const endCursor =
    pageInfo?.endCursor || edges[edges.length - 1]?.cursor || null;

  return { orders, pageInfo, endCursor };
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCsvDate(csvFileName) {
  if (!csvFileName) return "-";

  const match = String(csvFileName).match(/(\d{4})(\d{2})(\d{2})/);
  if (!match) return "-";

  const [, year, month, day] = match;
  return `${year}-${month}-${day}`;
}

function isUnfulfilledStatus(status) {
  if (!status) return false;
  return String(status).toLowerCase().includes("unfulfill");
}

function isOverdueAfterProcessedAt(processedAt, days = 3) {
  if (!processedAt) return false;

  const processedDate = new Date(processedAt);
  if (Number.isNaN(processedDate.getTime())) return false;

  const diffMs = Date.now() - processedDate.getTime();
  return diffMs >= days * 24 * 60 * 60 * 1000;
}

function isOverdueUnfulfilledOrder(order) {
  return (
    isUnfulfilledStatus(order?.displayFulfillmentStatus) &&
    isOverdueAfterProcessedAt(order?.processedAt, 3)
  );
}

function isOverdueAwaitingAlcOrder(order) {
  return (
    !order?.wisInfo?.csv && isOverdueAfterProcessedAt(order?.processedAt, 2)
  );
}

function formatFulfillmentStatus(status, isOverdueUnfulfilled = false) {
  if (!status) return "-";
  const text = String(status);
  const normalized = text.toLowerCase();
  if (normalized.includes("unfulfill")) {
    return `${isOverdueUnfulfilled ? "🔴" : "🟠"} ${text}`;
  }
  if (normalized.includes("fulfill")) return `🟢 ${text}`;
  return text;
}

function buildItemQtyMap(items, getNumber, getQty) {
  const map = new Map();
  items.forEach((item) => {
    const rawNumber = getNumber(item);
    const number = String(rawNumber || "").trim();
    if (!number) return;
    const qty = Number(getQty(item)) || 0;
    map.set(number, (map.get(number) || 0) + qty);
  });
  return map;
}

function getOrderXmlItemsWithDiff(order) {
  const xmlItems = order?.wisInfo?.alc_xml_info?.items;

  if (!Array.isArray(xmlItems) || xmlItems.length === 0) return [];

  const lineItemEdges = Array.isArray(order?.lineItems?.edges)
    ? order.lineItems.edges
    : [];

  const orderQtyMap = buildItemQtyMap(
    lineItemEdges,
    (edge) => edge?.node?.sku,
    (edge) => edge?.node?.quantity,
  );
  const xmlQtyMap = buildItemQtyMap(
    xmlItems,
    (item) => item?.item_number,
    (item) => item?.quantity,
  );

  return [...xmlQtyMap.keys()]
    .map((itemNumber) => {
      const orderQty = orderQtyMap.get(itemNumber) || 0;
      const xmlQty = xmlQtyMap.get(itemNumber) || 0;
      return {
        itemNumber,
        xmlQty,
        isDifferent: orderQty !== xmlQty,
      };
    })
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber));
}

function getShopifyOrderQuery(statusFilter) {
  return ORDER_STATUS_FILTERS[statusFilter] || ORDER_STATUS_FILTERS.any;
}

const ShopifyOrdersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    cursor: "",
    cursorHistory: "[]",
    orderStatus: "open",
  });
  const params = Object.fromEntries(searchParams.entries());
  const cursor = params.cursor || null;
  const orderStatus = params.orderStatus || "open";
  let cursorHistory = [];
  try {
    const parsedCursorHistory = JSON.parse(params.cursorHistory || "[]");
    cursorHistory = Array.isArray(parsedCursorHistory)
      ? parsedCursorHistory
      : [];
  } catch {
    cursorHistory = [];
  }

  const { loading, error, data } = useQuery(FETCH_SHOPIFY_ORDERS, {
    variables: {
      instance: "AB",
      pageSize: PAGE_SIZE,
      cursor,
      query: getShopifyOrderQuery(orderStatus),
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const parsed = parseResponse(data?.shopifyOrders);
  const parseError =
    !loading && !error && data && parsed === null
      ? new Error("Failed to parse shopifyOrders data.")
      : null;

  const { orders, pageInfo, endCursor } = normalizeOrdersPayload(parsed);
  console.log("orders", orders);

  const hasPreviousPage = cursorHistory.length > 0;
  const canLoadNext = Boolean(pageInfo?.hasNextPage && endCursor);
  const pageNumber = cursorHistory.length + 1;
  const from = orders.length ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
  const to = (pageNumber - 1) * PAGE_SIZE + orders.length;

  function goNextPage() {
    if (!canLoadNext) return;
    setSearchParams({
      ...params,
      cursor: endCursor,
      cursorHistory: JSON.stringify([...cursorHistory, cursor]),
    });
  }

  function goPreviousPage() {
    if (!hasPreviousPage) return;
    const nextCursorHistory = [...cursorHistory];
    const previousCursor = nextCursorHistory.pop();
    setSearchParams({
      ...params,
      cursor: previousCursor || "",
      cursorHistory: JSON.stringify(nextCursorHistory),
    });
  }
  if (error) return <Errors error={error} />;
  if (parseError) return <Errors error={parseError} />;

  return (
    <PageV2
      title="Shopify EU Orders"
      toolbar={
        <div>
          <Link to="/tools/shopify-alc" target="_blank">
            Shopify &amp; ALC Tool
          </Link>
        </div>
      }
    >
      <div className="h-full flex flex-col space-y-4">
        <div className="flex items-center space-x-4">
          <Select
            value={orderStatus}
            onChange={(e) =>
              setSearchParams({
                ...params,
                orderStatus: e.target.value,
                cursor: "",
                cursorHistory: "[]",
              })
            }
          >
            <option value="any">All Orders</option>
            <option value="open">Open Orders</option>
            <option value="closed">Closed Orders</option>
            <option value="cancelled">Cancelled Orders</option>
          </Select>

          <div className="flex-1">
            <SearchBar />
          </div>
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto">
            {loading ? (
              <Spinner />
            ) : (
              <table className="whitespace-nowrap min-w-full">
                <thead>
                  <tr className="sticky top-0 backdrop-blur-xl dark:bg-opacity-90 z-10">
                    <th className="text-left px-4 py-3">
                      Order ({orders.length})
                    </th>
                    <th className="text-left px-4 py-3">Products</th>
                    <th className="text-right px-4 py-3">Fulfillment Status</th>
                    <th className="text-right px-4 py-3">ALC Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => {
                    const isOverdueUnfulfilled =
                      isOverdueUnfulfilledOrder(order);
                    const isOverdueAwaitingAlc =
                      isOverdueAwaitingAlcOrder(order);
                    const xmlItemsWithDiff = getOrderXmlItemsWithDiff(order);
                    const hasDifferentXmlItems = xmlItemsWithDiff.some(
                      (item) => item.isDifferent,
                    );

                    return (
                      <tr
                        key={index}
                        className={`border-y border-gray-100 dark:border-gray-700
                        ${order.cancelledAt ? "opacity-70 line-through" : ""}
                        ${order?.closedAt ? " bg-gray-200/70 border-gray-200" : ""}
                        `}
                      >
                        <td className="px-4">
                          <div className="space-y-1">
                            <div className="opacity-70">
                              {formatDate(order?.processedAt)}(Sweden)
                            </div>
                            <div>
                              <span className="font-semibold">
                                {order.closedAt
                                  ? "✔ "
                                  : isOverdueUnfulfilled
                                    ? "🔴 "
                                    : "🟠 "}
                                {order.name}
                              </span>
                              <a
                                className="ml-2 font-normal"
                                href={order.adminUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                Open in Shopify
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="px-4">
                          <OrderLineItemsProductView
                            lineItems={order.lineItems.edges}
                            showWarnings={!order.closedAt}
                          />
                        </td>
                        <td className="px-4 text-right">
                          <div>
                            <div>
                              {formatFulfillmentStatus(
                                order?.displayFulfillmentStatus,
                                isOverdueUnfulfilled || hasDifferentXmlItems,
                              )}
                            </div>
                            <div className="mt-1">
                              <div className="flex flex-wrap justify-end">
                                {xmlItemsWithDiff.map((item) => (
                                  <span
                                    key={item.itemNumber}
                                    className={`ml-1
                                            ${
                                              item.isDifferent
                                                ? "text-red-600"
                                                : ""
                                            }`}
                                  >
                                    {item.itemNumber}({item.xmlQty})
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 text-right">
                          {order.wisInfo?.csv ? (
                            <div>
                              <span>🟢 Sent to ALC</span>
                              <span className="pl-1 opacity-70">
                                ({formatCsvDate(order.wisInfo.csv)})
                              </span>
                            </div>
                          ) : isOverdueAwaitingAlc ? (
                            "🔴 Awaiting to send to ALC"
                          ) : (
                            "🟠 Awaiting to send to ALC"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!orders.length ? (
                    <tr>
                      <td className="px-4 py-6 opacity-70" colSpan={6}>
                        No orders found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
          </div>

          {!loading && (
            <div className="p-3 flex justify-end items-center space-x-2 opacity-70">
              {hasPreviousPage ? (
                <Button prev onClick={goPreviousPage} />
              ) : null}
              <div>
                {from}-{to}
              </div>
              {canLoadNext ? <Button next onClick={goNextPage} /> : null}
            </div>
          )}
        </div>

        <div>
          Ⓘ WIS automatically sends shopify open orders to ALC warehouse and
          sends the fulfilled orders status back to Shopify at 08:00 China time
          / 02:00 Europe time every day
        </div>
      </div>
    </PageV2>
  );
};

export default ShopifyOrdersPage;
